import axios from 'axios';
import { IAppReducer, IDetailStep } from 'models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  createSub,
  getBuyPlanCompanies,
  getBuyPlanCompanyParams,
  getDataByKey,
  getProductsByCompany,
} from 'shared/services/user-add-subscription';
import {
  Advantage,
  ApiProperty,
  BuyPlanCompany,
  Coupon,
  Price,
  Product,
  VehicleData,
} from '../types/Add-subscription.types';
import {
  getPayment,
  getProperties,
  getSub,
} from '../utils/Add-subscription.util';

interface UseAddSubscriptionReturn {
  steps: IDetailStep[];
  navigate: ReturnType<typeof useNavigate>;
  products: Product[];
  vehicleData: VehicleData;
  /** true si falló la carga de marcas y modelos (brand_and_models); independiente del error del catálogo. */
  vehicleDataError: boolean;
  retryLoadVehicleData: () => void;
  loading: boolean;
  error: boolean;
  user: IAppReducer['authReducer'];
  period: 'Mensual' | 'Anual';
  counts: Record<number, number>;
  selectedAddons: Record<number, boolean>;
  isFormValid: boolean;
  formData: Record<string, Record<string, string>>;
  handleCount: (price: Price, increment: boolean) => void;
  handleAddonToggle: (priceId: number) => void;
  calculatePrice: (price: Price) => number;
  formatCurrency: (amount: number, currency: string) => string;
  getAdvantages: (product: Product, price: Price) => Advantage[];
  handlePurchase: () => void;
  handleFormValidChange: (isValid: boolean) => void;
  isAnyProductSelected: boolean;
  setPeriod: React.Dispatch<React.SetStateAction<'Mensual' | 'Anual'>>;
  setFormData: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, string>>>
  >;
  subtotal: number;
  isPurchasing: boolean;
  showAlertError: null | string;
  setShowAlertError: React.Dispatch<React.SetStateAction<null | string>>;
  // TEMPORAL — eliminar con CB-186
  /** checking: se evalúa si aplica el selector; visible/hidden: resultado. */
  companySelectorStatus: CompanySelectorStatus;
  /** Opciones del selector: companies de buy_plan_companies del país de la cuenta. */
  companies: BuyPlanCompany[];
  /** Company con la que se lista el catálogo; por defecto la de la cuenta. */
  selectedCompanyId: number;
  handleCompanyChange: (companyId: number) => void;
  retryLoadProducts: () => void;
  /** Nombre comercial de la company en que se creó la sub (aviso de éxito con selector). */
  successCompanyName: string | null;
  finishPurchase: () => void;
}

// TEMPORAL — eliminar con CB-186
type CompanySelectorStatus = 'checking' | 'visible' | 'hidden';

const buildSteps = (
  subscriptionsEnabled: boolean,
  returnUrl?: string
): IDetailStep[] => [
  {
    description: 'ajustes',
    state: 'active',
    link: '/settings',
    permissionToNavigate: true,
    // Cuando el panel de suscripciones de Soda es el autoritativo, todo lo
    // demás de Settings tampoco aplica para este agente — lo mandamos de
    // vuelta a Soda en vez de al menú de ajustes de Meraki.
    ...(subscriptionsEnabled && returnUrl ? { externalHref: returnUrl } : {}),
  },
  {
    description: 'suscripciones',
    state: 'active',
    link: '/settings/subscriptions',
    permissionToNavigate: true,
    // Misma razón: la lista vieja de Meraki queda bloqueada (ver
    // SubscriptionsGuard), así que el click regresa directo a Soda.
    ...(subscriptionsEnabled && returnUrl ? { externalHref: returnUrl } : {}),
  },
  {
    description: 'agregar',
    state: 'active',
    link: '/settings/subscriptions/add-subscription',
    permissionToNavigate: false,
  },
];

// TEMPORAL — eliminar con CB-186
/**
 * CB-190: el selector «Compañía de la suscripción» aparece solo si se cumplen todas:
 * - el agente tiene rol customer service o admin (agentAuthData.role),
 * - el agente es de Connect PR (agentAuthData.companyParam, de agentInfo en /v2/backoffice/impersonate),
 * - la llave buy_plan_companies tiene companies para el país de la cuenta,
 * - la company de la cuenta impersonada está en esa lista.
 * Si no, la pantalla queda igual que antes.
 */
const COMPANY_SELECTOR_AGENT_ROLES = ['customer service', 'admin'];
const COMPANY_SELECTOR_AGENT_COMPANY_PARAM = 'connectpr';
/** Códigos del back (CB-185) que significan que el plan o el cupón no aplican a la company elegida. */
const COMPANY_REJECTION_CODES = [
  'PRICES_NOT_IN_COMPANY',
  'COUPON_NOT_IN_COMPANY',
  'COMPANY_NOT_ALLOWED',
];

/** Nombre comercial de una company: alias_name si existe, si no name ('' sin company). */
export const getCompanyDisplayName = (company?: BuyPlanCompany | null) =>
  company ? company.alias_name?.trim() || company.name : '';

const initVehicleData = {
  autos: { '': [''] },
  motos: { '': [''] },
};

interface UseAddSubscriptionProps {
  appliedCoupon: Coupon | null;
  selectedPaymentMethod: string | null;
  selectedPropertyByKey?: Record<string, ApiProperty | null>;
}

export const useAddSubscription = ({
  appliedCoupon,
  selectedPaymentMethod,
  selectedPropertyByKey = {},
}: UseAddSubscriptionProps): UseAddSubscriptionReturn => {
  const navigate = useNavigate();
  const { authReducer: user } = useSelector((state: IAppReducer) => state);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [vehicleData, setVehicleData] = useState<VehicleData>(initVehicleData);
  const [vehicleDataError, setVehicleDataError] = useState<boolean>(false);
  const [vehicleDataReloadKey, setVehicleDataReloadKey] = useState(0);
  const [isPurchasing, setIsPurchasing] = useState(false);

  const [period, setPeriod] = useState<'Mensual' | 'Anual'>('Anual');
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [selectedAddons, setSelectedAddons] = useState<Record<number, boolean>>(
    {}
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [formData, setFormData] = useState<
    Record<string, Record<string, string>>
  >({});
  const [showAlertError, setShowAlertError] = useState<null | string>(null);
  // TEMPORAL — eliminar con CB-186
  const agentAuthData = user.agentAuthData;
  const isAgentAllowedToSelectCompany =
    Boolean(agentAuthData?.accessToken) &&
    COMPANY_SELECTOR_AGENT_ROLES.includes(agentAuthData?.role ?? '') &&
    agentAuthData?.companyParam === COMPANY_SELECTOR_AGENT_COMPANY_PARAM;
  const [companySelectorStatus, setCompanySelectorStatus] =
    useState<CompanySelectorStatus>(
      isAgentAllowedToSelectCompany ? 'checking' : 'hidden'
    );
  const [companies, setCompanies] = useState<BuyPlanCompany[]>([]);
  // Por defecto la company de la cuenta, con o sin selector (con selector viene preseleccionada).
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>(
    Number(user.companyId)
  );
  const [productsReloadKey, setProductsReloadKey] = useState(0);
  const [successCompanyName, setSuccessCompanyName] = useState<string | null>(
    null
  );

  const steps = useMemo(
    () =>
      buildSteps(
        Boolean(user.agentAuthData?.subscriptionsEnabled),
        user.agentAuthData?.returnUrl
      ),
    [user.agentAuthData?.subscriptionsEnabled, user.agentAuthData?.returnUrl]
  );

  // Marcas y modelos para los formularios de vehículo. Su error es independiente del catálogo.
  useEffect(() => {
    let cancelled = false;
    setVehicleDataError(false);
    getDataByKey('brand_and_models')
      .then((data) => {
        if (!cancelled) setVehicleData(data);
      })
      .catch(() => {
        if (!cancelled) setVehicleDataError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [vehicleDataReloadKey]);

  /** Vuelve a pedir brand_and_models sin tocar el catálogo. */
  const retryLoadVehicleData = useCallback(
    () => setVehicleDataReloadKey((key) => key + 1),
    []
  );

  // TEMPORAL — eliminar con CB-186
  // Decide si aplica el selector (llave del país + company de la cuenta en la lista). Ante cualquier
  // fallo se oculta y la pantalla sigue con la company de la cuenta.
  useEffect(() => {
    if (!isAgentAllowedToSelectCompany) return;
    const hideSelector = () => {
      setCompanies([]);
      setSelectedCompanyId(Number(user.companyId));
      setCompanySelectorStatus('hidden');
    };
    Promise.all([
      getBuyPlanCompanyParams(user.countryAcronym),
      getBuyPlanCompanies(),
    ])
      .then(([allowedParams, allCompanies]) => {
        const userCompany = allCompanies.find(
          (company) => company.id === Number(user.companyId)
        );
        const options = allCompanies.filter(
          (company) => !!company.param && allowedParams.includes(company.param)
        );
        const userCompanyAllowed =
          !!userCompany?.param && allowedParams.includes(userCompany.param);
        if (!userCompanyAllowed || options.length === 0) {
          hideSelector();
          return;
        }
        setCompanies(options);
        setCompanySelectorStatus('visible');
      })
      .catch(hideSelector);
  }, [isAgentAllowedToSelectCompany, user.companyId, user.countryAcronym]);

  // Prices de la company elegida; las reglas de ocultamiento se aplican igual, con esa company.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getProductsByCompany(selectedCompanyId, user.agentAuthData?.email)
      .then((response) => {
        if (!cancelled) setProducts(response);
      })
      .catch(() => {
        if (!cancelled) {
          setProducts([]);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, productsReloadKey]);

  // TEMPORAL — eliminar con CB-186
  /** Vuelve a pedir el catálogo de la company seleccionada; solo afecta el error del catálogo. */
  const retryLoadProducts = useCallback(
    () => setProductsReloadKey((key) => key + 1),
    []
  );

  // TEMPORAL — eliminar con CB-186
  /** Cambia la company del catálogo y vacía cantidades, add-ons y formularios. */
  const handleCompanyChange = useCallback((companyId: number) => {
    setSelectedCompanyId(companyId);
    setCounts({});
    setSelectedAddons({});
    setFormData({});
    setIsFormValid(false);
  }, []);

  useEffect(() => {
    const defaultCounts = products
      .filter((product) => !product.is_addon)
      .reduce((acc, product) => {
        product.prices.forEach((price) => {
          if (price.periocity_id.name === period) {
            acc[price.id] = 0;
          }
        });
        return acc;
      }, {} as Record<number, number>);
    setCounts((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(defaultCounts)) {
        return defaultCounts;
      }
      return prev;
    });
  }, [products, period]);

  const handleCount = (price: Price, increment: boolean) => {
    setCounts((prev) => {
      const currentCount = prev[price.id] || 0;
      let newCount = increment ? currentCount + 1 : currentCount - 1;

      newCount = Math.max(0, Math.min(newCount, price.max_count));

      if (newCount !== currentCount) {
        return { ...prev, [price.id]: newCount };
      }
      return prev;
    });
  };

  const handleAddonToggle = useCallback((priceId: number) => {
    setSelectedAddons((prev) => ({
      ...prev,
      [priceId]: !prev[priceId],
    }));
  }, []);

  const calculatePrice = useCallback(
    (price: Price) => {
      const count = counts[price.id] || 0;
      const basePrice =
        count < price.max_additional
          ? price.price * count
          : price.additional_price * count;
      return basePrice;
    },
    [counts, appliedCoupon]
  );

  const formatCurrency = (amount: number, currency: string): string => {
    const formatter = new Intl.NumberFormat('es-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return formatter.format(amount);
  };

  const getAdvantages = (product: Product, price: Price): Advantage[] => {
    return price.advantages || product.advantages;
  };

  const handleFormValidChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  /** Sale de la pantalla tras la compra: vuelve a Soda (returnUrl) o a la lista de suscripciones. */
  const finishPurchase = () => {
    setSuccessCompanyName(null);
    if (
      user.agentAuthData?.subscriptionsEnabled &&
      user.agentAuthData?.returnUrl
    ) {
      globalThis.location.href = user.agentAuthData.returnUrl;
      return;
    }
    navigate('/settings/subscriptions', {
      state: { showAlertSuccess: true },
      replace: true,
    });
  };

  const handlePurchase = async () => {
    setIsPurchasing(true);
    try {
      const baseSub = getSub({
        customerAuthId: user.uid,
        agentAuthId: user.agentAuthData ? user.agentAuthData.uid : '',
        products,
        period: period,
        counts,
        appliedCoupon,
        selectedAddons,
        sourceApp: user.agentAuthData?.sourceApp ?? 'meraki-frontend',
        sourceOrigin:
          user.agentAuthData?.sourceOrigin ?? window.location.origin + '/',
      });
      // TEMPORAL — eliminar con CB-186: con selector se envía la company elegida si no es la de la cuenta (CB-185).
      const sub =
        companySelectorStatus === 'visible' &&
        selectedCompanyId !== Number(user.companyId)
          ? { ...baseSub, companyId: selectedCompanyId }
          : baseSub;
      const properties = getProperties({
        formData,
        products,
        period,
        selectedAddons,
        selectedPropertyByKey,
      });
      const payment = getPayment({
        user,
        cardId: selectedPaymentMethod,
      });
      await createSub({
        properties,
        sub,
        authId: user.uid,
        payment,
      });
      // TEMPORAL — eliminar con CB-186: con selector se avisa en qué company quedó antes de salir.
      if (companySelectorStatus === 'visible') {
        setSuccessCompanyName(
          getCompanyDisplayName(
            companies.find((company) => company.id === selectedCompanyId)
          )
        );
        return;
      }
      finishPurchase();
    } catch (error) {
      console.error('Error creating subscription:', error);
      const serverMessage =
        axios.isAxiosError(error) && error?.response?.data?.error?.message;
      const serverCode = axios.isAxiosError(error)
        ? error?.response?.data?.error?.details?.code
        : undefined;
      // TEMPORAL — eliminar con CB-186
      if (
        companySelectorStatus === 'visible' &&
        COMPANY_REJECTION_CODES.includes(serverCode)
      ) {
        const companyName = getCompanyDisplayName(
          companies.find((company) => company.id === selectedCompanyId)
        );
        setShowAlertError(
          `Este plan o cupón no está disponible para ${companyName}. Revisa la compañía elegida.`
        );
      } else if (serverMessage) {
        if (serverMessage.toLowerCase() === 'cx not found') {
          setShowAlertError(
            'Error al crear la suscripción, cierre sesión e inicie nuevamente'
          );
        } else {
          setShowAlertError(serverMessage);
        }
      } else {
        setShowAlertError('Error al crear la suscripción');
      }
    } finally {
      setIsPurchasing(false);
    }
  };

  const isAnyProductSelected: boolean = Object.values(counts).some(
    (count) => count > 0
  );

  const subtotal = useMemo(() => {
    let baseTotal = products.reduce((total, product) => {
      if (!product.is_addon) {
        return (
          total +
          product.prices.reduce((productTotal, price) => {
            if (price.periocity_id.name === period) {
              return productTotal + calculatePrice(price);
            }
            return productTotal;
          }, 0)
        );
      }
      return total;
    }, 0);

    products.forEach((product) => {
      if (product.is_addon) {
        product.prices.forEach((price) => {
          if (selectedAddons[price.id] && price.periocity_id.name === period) {
            const relatedProductCount = products
              .filter((p) => !p.is_addon)
              .reduce((count, p) => {
                return (
                  count +
                  p.prices.reduce((pCount, pPrice) => {
                    if (
                      pPrice.product_type_id.name ===
                        price.product_type_id.name &&
                      pPrice.periocity_id.name === period
                    ) {
                      return pCount + (counts[pPrice.id] || 0);
                    }
                    return pCount;
                  }, 0)
                );
              }, 0);

            if (relatedProductCount < price.max_additional) {
              baseTotal += price.price * relatedProductCount;
            } else {
              baseTotal += price.additional_price * relatedProductCount;
            }
          }
        });
      }
    });

    return baseTotal;
  }, [products, selectedAddons, period, calculatePrice, counts]);

  return {
    steps,
    navigate,
    products,
    vehicleData,
    vehicleDataError,
    retryLoadVehicleData,
    loading,
    error,
    user,
    period,
    counts,
    selectedAddons,
    isFormValid,
    formData,
    handleCount,
    handleAddonToggle,
    calculatePrice,
    formatCurrency,
    getAdvantages,
    handlePurchase,
    handleFormValidChange,
    isAnyProductSelected,
    setPeriod,
    setFormData,
    subtotal,
    isPurchasing,
    showAlertError,
    setShowAlertError,
    companySelectorStatus,
    companies,
    selectedCompanyId,
    handleCompanyChange,
    retryLoadProducts,
    successCompanyName,
    finishPurchase,
  };
};
